import { readFileSync, readdirSync, existsSync } from "node:fs";
import { join, basename } from "node:path";
import { homedir } from "node:os";
import { countTokens } from "@anthropic-ai/tokenizer";
import type { NormalizedSession, TokenSource } from "./types.js";

/**
 * Parse a single JSONL session file into a normalized record.
 */
export function parseSessionFile(filePath: string): NormalizedSession | null {
  const content = readFileSync(filePath, "utf-8");
  const lines = content.trim().split("\n").filter(Boolean);

  if (lines.length === 0) return null;

  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let cacheCreateTokens = 0;
  let toolCalls = 0;
  let model = "unknown";
  let hasApiUsage = false;
  let sessionStart = 0;
  let sessionEnd = 0;
  const filesTouched = new Set<string>();
  const conversationParts: string[] = [];
  const inputParts: string[] = [];
  const outputParts: string[] = [];

  for (const line of lines) {
    let entry: Record<string, unknown>;
    try {
      entry = JSON.parse(line);
    } catch {
      continue;
    }

    // Extract timestamp
    const ts = entry.timestamp as string | undefined;
    if (ts) {
      const time = new Date(ts).getTime();
      if (!sessionStart || time < sessionStart) sessionStart = time;
      if (time > sessionEnd) sessionEnd = time;
    }

    // Extract usage
    const usage = entry.usage as Record<string, number> | undefined;
    if (usage) {
      inputTokens += usage.input_tokens ?? 0;
      outputTokens += usage.output_tokens ?? 0;
      cacheReadTokens += usage.cache_read_input_tokens ?? 0;
      cacheCreateTokens += usage.cache_creation_input_tokens ?? 0;
      if ((usage.input_tokens ?? 0) > 0 || (usage.output_tokens ?? 0) > 0) {
        hasApiUsage = true;
      }
    }

    // Extract model
    if (entry.model && typeof entry.model === "string") {
      model = entry.model;
    }

    // Count tool calls
    if (entry.type === "tool_use" || entry.tool_name) {
      toolCalls++;
    }

    // Extract files from tool calls (Read, Write, Edit)
    const toolInput = entry.tool_input as Record<string, unknown> | undefined;
    if (toolInput) {
      const fp = toolInput.file_path ?? toolInput.path;
      if (typeof fp === "string") filesTouched.add(fp);
    }

    // Extract conversation text for keyword matching + token estimation.
    // Track input (user) vs output (assistant) separately for accurate fallback.
    const entryRole = entry.role as string | undefined;
    const entryType = entry.type as string | undefined;
    const isInput = entryRole === "user" || entryType === "user";
    const isOutput = entryRole === "assistant" || entryType === "assistant";
    const bucket = isInput ? inputParts : isOutput ? outputParts : conversationParts;

    const message = entry.message as Record<string, unknown> | undefined;
    if (message && typeof message === "object") {
      const content = message.content;
      if (typeof content === "string") {
        bucket.push(content);
      } else if (Array.isArray(content)) {
        for (const part of content) {
          if (typeof part === "object" && part !== null && "text" in part) {
            bucket.push((part as { text: string }).text);
          }
        }
      }
    }

    if (isInput || isOutput) {
      const text = entry.content ?? entry.text;
      if (typeof text === "string") bucket.push(text);
    }
  }

  const sessionId = basename(filePath, ".jsonl");
  const activeMinutes =
    sessionStart && sessionEnd ? (sessionEnd - sessionStart) / 60000 : undefined;
  const allParts = [...inputParts, ...outputParts, ...conversationParts];
  const conversationText = allParts.join("\n");

  // Determine token source and apply fallback if needed.
  let tokenSource: TokenSource;

  if (hasApiUsage) {
    tokenSource = "api";
  } else if (conversationText.length > 0) {
    // Fallback: count tokens from conversation text when usage data is missing.
    // This handles subagent sessions where Claude Code doesn't write usage blocks.
    // Uses Anthropic's official tokenizer for accurate counts, split by role.
    tokenSource = "tokenizer";
    const inputText = inputParts.join("\n");
    const outputText = outputParts.join("\n");
    const unattributedText = conversationParts.join("\n");

    if (inputText.length > 0 || outputText.length > 0) {
      inputTokens = inputText.length > 0 ? countTokens(inputText) : 0;
      outputTokens = outputText.length > 0 ? countTokens(outputText) : 0;
      if (unattributedText.length > 0) {
        const unattributed = countTokens(unattributedText);
        inputTokens += Math.round(unattributed * 0.3);
        outputTokens += Math.round(unattributed * 0.7);
      }
    } else {
      const total = countTokens(conversationText);
      inputTokens = Math.round(total * 0.3);
      outputTokens = Math.round(total * 0.7);
    }
  } else {
    tokenSource = "none";
  }

  return {
    sessionId,
    provider: "anthropic",
    model,
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheCreateTokens,
    toolCalls,
    filesTouched: Array.from(filesTouched),
    sessionStart,
    sessionEnd: sessionEnd || undefined,
    activeMinutes,
    conversationText,
    tokenSource,
  };
}

/**
 * Find all JSONL session files for a project path.
 */
export function findSessionFiles(projectPath?: string): string[] {
  const baseDir = projectPath ?? join(homedir(), ".claude", "projects");
  if (!existsSync(baseDir)) return [];

  const files: string[] = [];

  function walk(dir: string) {
    const entries = readdirSync(dir, { withFileTypes: true });
    for (const entry of entries) {
      const fullPath = join(dir, entry.name);
      if (entry.isDirectory()) {
        walk(fullPath);
      } else if (entry.name.endsWith(".jsonl")) {
        files.push(fullPath);
      }
    }
  }

  walk(baseDir);
  return files;
}
